import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCompare } from '@/context/CompareContext';
import { Property } from '@/mocks/properties';

interface RowProps {
  label: string;
  icon: string;
  values: (string | number | boolean | string[])[];
  highlight?: boolean;
}

const CompareRow = ({ label, icon, values, highlight = false }: RowProps) => {
  const renderValue = (val: string | number | boolean | string[]) => {
    if (Array.isArray(val)) {
      return (
        <div className="flex flex-wrap gap-1.5 justify-center">
          {val.map((v, i) => (
            <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-100">
              {v}
            </span>
          ))}
        </div>
      );
    }
    if (typeof val === 'boolean') {
      return val
        ? <span className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full mx-auto"><i className="ri-check-line text-emerald-600 text-sm"></i></span>
        : <span className="w-6 h-6 flex items-center justify-center bg-stone-100 rounded-full mx-auto"><i className="ri-close-line text-stone-400 text-sm"></i></span>;
    }
    return <span className="text-stone-700 text-sm font-medium">{val}</span>;
  };

  return (
    <tr className={highlight ? 'bg-amber-50/40' : 'bg-white hover:bg-stone-50/60 transition-colors'}>
      <td className="px-5 py-3.5 border-b border-stone-100 w-44">
        <div className="flex items-center gap-2 text-stone-500 text-xs font-semibold uppercase tracking-wide">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${icon} text-stone-400`}></i>
          </div>
          {label}
        </div>
      </td>
      {values.map((val, i) => (
        <td key={i} className="px-5 py-3.5 border-b border-stone-100 text-center align-middle">
          {renderValue(val)}
        </td>
      ))}
      {/* Fill empty columns if less than 3 */}
      {Array.from({ length: 3 - values.length }).map((_, i) => (
        <td key={`empty-${i}`} className="px-5 py-3.5 border-b border-stone-100 text-center text-stone-300 text-sm">—</td>
      ))}
    </tr>
  );
};

const EmptySlot = ({ index }: { index: number }) => {
  const navigate = useNavigate();
  return (
    <th className="px-5 py-5 text-center font-normal border-b border-stone-100">
      <button
        onClick={() => navigate('/listings')}
        className="w-full flex flex-col items-center gap-3 p-6 border-2 border-dashed border-stone-200 rounded-2xl hover:border-amber-300 hover:bg-amber-50/30 transition-all cursor-pointer group"
      >
        <div className="w-12 h-12 flex items-center justify-center bg-stone-100 group-hover:bg-amber-100 rounded-full transition-colors">
          <i className="ri-add-line text-xl text-stone-400 group-hover:text-amber-500 transition-colors"></i>
        </div>
        <span className="text-stone-400 group-hover:text-amber-600 text-sm font-medium transition-colors">
          Add Property {index + 1}
        </span>
      </button>
    </th>
  );
};

const ComparePage = () => {
  const { compareList, removeFromCompare, clearCompare } = useCompare();
  const navigate = useNavigate();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Compare Properties | MyAbode',
      description: 'Compare up to 3 premium property listings side by side on MyAbode. Evaluate price, size, bedrooms, amenities, and more.',
      url: `${siteUrl}/compare`,
      isPartOf: { '@id': `${siteUrl}/#website` },
    };
    const id = 'schema-compare';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);
    document.title = 'Compare Properties Side by Side | MyAbode';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Compare up to 3 premium property listings side by side on MyAbode. Evaluate price, size, bedrooms, amenities, and more to find your perfect home.');
    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  const formatPrice = (p: Property) => {
    if (p.priceType === 'rent') return `$${p.price.toLocaleString()}/mo`;
    return `$${(p.price / 1000).toFixed(0)}K`;
  };

  const rows: { label: string; icon: string; key: keyof Property | 'price_fmt'; highlight?: boolean }[] = [
    { label: 'Price', icon: 'ri-price-tag-3-line', key: 'price_fmt', highlight: true },
    { label: 'Type', icon: 'ri-home-4-line', key: 'type' },
    { label: 'City', icon: 'ri-map-pin-line', key: 'city' },
    { label: 'Bedrooms', icon: 'ri-hotel-bed-line', key: 'bedrooms', highlight: true },
    { label: 'Bathrooms', icon: 'ri-drop-line', key: 'bathrooms' },
    { label: 'Size (sqft)', icon: 'ri-layout-line', key: 'sqft', highlight: true },
    { label: 'Year Built', icon: 'ri-calendar-line', key: 'yearBuilt' },
    { label: 'Rating', icon: 'ri-star-line', key: 'rating', highlight: true },
    { label: 'Amenities', icon: 'ri-list-check-2', key: 'amenities' },
  ];

  const getValue = (property: Property, key: string): string | number | boolean | string[] => {
    if (key === 'price_fmt') return formatPrice(property);
    if (key === 'sqft') return `${property.sqft.toLocaleString()} sqft`;
    if (key === 'rating') return `${property.rating} / 5.0`;
    return (property as Record<string, unknown>)[key] as string | number | boolean | string[];
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-[72px] pb-24">
        {/* Header */}
        <div className="bg-stone-900 py-12 px-4 md:px-8">
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white mb-1">Compare Properties</h1>
              <p className="text-stone-400 text-sm">Side-by-side comparison of up to 3 properties</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/listings')}
                className="px-4 py-2 bg-stone-700 hover:bg-stone-600 text-white text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Listings
              </button>
              {compareList.length > 0 && (
                <button
                  onClick={clearCompare}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {compareList.length === 0 ? (
            /* Empty State */
            <div className="text-center py-24">
              <div className="w-20 h-20 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-5">
                <i className="ri-scales-line text-3xl text-stone-400"></i>
              </div>
              <h2 className="text-stone-700 text-xl font-bold mb-2">No properties to compare</h2>
              <p className="text-stone-400 text-sm mb-6">Go to listings and click the scale icon on any property card to add it here.</p>
              <button
                onClick={() => navigate('/listings')}
                className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-lg transition-colors cursor-pointer"
              >
                Browse Listings
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-stone-100">
                      {/* Label column header */}
                      <th className="px-5 py-5 text-left w-44">
                        <span className="text-xs font-semibold text-stone-400 uppercase tracking-wide">Attribute</span>
                      </th>

                      {/* Property columns */}
                      {compareList.map(property => (
                        <th key={property.id} className="px-5 py-5 text-center font-normal border-b border-stone-100 min-w-[220px]">
                          <div className="relative">
                            {/* Remove button */}
                            <button
                              onClick={() => removeFromCompare(property.id)}
                              className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-stone-100 hover:bg-red-100 hover:text-red-500 text-stone-400 rounded-full transition-colors cursor-pointer z-10"
                              title="Remove from compare"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>

                            {/* Property image */}
                            <div className="w-full h-36 rounded-xl overflow-hidden mb-3">
                              <img
                                src={property.image}
                                alt={property.title}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>

                            {/* Badges */}
                            <div className="flex items-center justify-center gap-1.5 mb-2">
                              {property.isNew && (
                                <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full">New</span>
                              )}
                              {property.isFeatured && (
                                <span className="px-2 py-0.5 bg-stone-800 text-white text-xs font-semibold rounded-full">Featured</span>
                              )}
                            </div>

                            <h3 className="text-stone-800 font-bold text-sm leading-snug mb-1 line-clamp-2">{property.title}</h3>
                            <p className="text-stone-400 text-xs flex items-center justify-center gap-1 mb-3">
                              <i className="ri-map-pin-line"></i>
                              {property.location}
                            </p>

                            <button
                              onClick={() => navigate(`/listings/${property.id}`)}
                              className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer"
                            >
                              View Details
                            </button>
                          </div>
                        </th>
                      ))}

                      {/* Empty slots */}
                      {Array.from({ length: 3 - compareList.length }).map((_, i) => (
                        <EmptySlot key={i} index={compareList.length + i} />
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map(row => (
                      <CompareRow
                        key={row.key}
                        label={row.label}
                        icon={row.icon}
                        values={compareList.map(p => getValue(p, row.key))}
                        highlight={row.highlight}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ComparePage;
